import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { sanitizeText } from "@/lib/sanitize";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalTextarea } from "@/components/ui/BrutalTextarea";
import { BrutalRadioGroup, BrutalRadioGroupItem } from "@/components/ui/BrutalRadioGroup";
import { Label } from "@/components/ui/label";
import { Flag, AlertTriangle } from "lucide-react";

interface ReportReviewModalProps {
  reviewId: string;
  reviewTitle: string;
  isOpen: boolean;
  onClose: () => void;
}

// Must match DB constraint: spam, fake, offensive, irrelevant, other
const REPORT_REASONS = [
  { value: "spam", labelKey: "reports.reasons.spam" },
  { value: "fake", labelKey: "reports.reasons.fake" },
  { value: "offensive", labelKey: "reports.reasons.offensive" },
  { value: "irrelevant", labelKey: "reports.reasons.irrelevant" },
  { value: "other", labelKey: "reports.reasons.other" },
];

export function ReportReviewModal({
  reviewId,
  reviewTitle,
  isOpen,
  onClose,
}: ReportReviewModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason) {
      toast.error(t("reports.selectReason", "Seleziona un motivo per la segnalazione"));
      return;
    }

    if (!user) {
      toast.error(t("auth.loginRequired", "Devi effettuare l'accesso"));
      return;
    }

    try {
      setSubmitting(true);

      const { error } = await supabase.from("review_reports").insert({
        review_id: reviewId,
        reported_by: user.id,
        report_reason: reason,
        report_details: details.trim() ? sanitizeText(details.trim()) : null,
        status: "pending",
      });

      if (error) throw error;

      toast.success(t("reports.success", "Segnalazione inviata con successo"));
      handleClose();
    } catch (error: any) {
      console.error("Error submitting report:", error);
      toast.error(t("reports.error", "Errore durante l'invio della segnalazione") + (error?.message ? `: ${error.message}` : ""));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setReason("");
    setDetails("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md border-[3px] border-dark shadow-[6px_6px_0px_#101322]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl">
            <Flag className="w-5 h-5 text-warning" />
            {t("reviews.reportReview", "Segnala recensione")}
          </DialogTitle>
          <DialogDescription>
            {t("reviews.reportDescription", "Segnala questa recensione se ritieni che violi le linee guida")}
          </DialogDescription>
        </DialogHeader>

        {/* Review being reported */}
        <div className="bg-surface p-3 border-[2px] border-dark">
          <p className="text-sm text-muted-foreground mb-1">
            {t("reviews.reviewBeingReported", "Recensione segnalata")}:
          </p>
          <p className="font-bold text-dark truncate">{reviewTitle}</p>
        </div>

        {/* Warning */}
        <div className="flex items-start gap-2 p-3 bg-warning/10 border-[2px] border-warning text-warning-foreground">
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-warning" />
          <p className="text-sm">
            {t("reviews.reportWarning", "Le segnalazioni false possono comportare conseguenze per il tuo account.")}
          </p>
        </div>

        {/* Reason selection */}
        <div className="space-y-3">
          <Label className="font-bold">
            {t("reports.reason", "Motivo della segnalazione")} *
          </Label>
          <BrutalRadioGroup value={reason} onValueChange={setReason}>
            {REPORT_REASONS.map((r) => (
              <div key={r.value} className="flex items-center space-x-2">
                <BrutalRadioGroupItem value={r.value} id={r.value} />
                <Label htmlFor={r.value} className="cursor-pointer">
                  {t(r.labelKey, r.value)}
                </Label>
              </div>
            ))}
          </BrutalRadioGroup>
        </div>

        {/* Additional details */}
        <div className="space-y-2">
          <Label className="font-bold">
            {t("reports.details", "Dettagli aggiuntivi")} ({t("common.optional", "opzionale")})
          </Label>
          <BrutalTextarea
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder={t("reports.detailsPlaceholder", "Fornisci ulteriori dettagli sulla segnalazione...")}
            rows={3}
            maxLength={500}
          />
          <p className="text-xs text-muted-foreground text-right">
            {details.length}/500
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <BrutalButton
            variant="secondary"
            onClick={handleClose}
            disabled={submitting}
            className="flex-1"
          >
            {t("common.cancel", "Annulla")}
          </BrutalButton>
          <BrutalButton
            variant="danger"
            onClick={handleSubmit}
            disabled={submitting || !reason}
            className="flex-1"
          >
            {submitting ? t("common.sending", "Invio...") : t("reports.submit", "Invia segnalazione")}
          </BrutalButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}
